<script setup>
import { ref, reactive, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import AgentBotsAPI from 'dashboard/api/agentBots';
import {
  AGENT_TEMPLATES,
  DEFAULT_TEMPLATE_KEY,
  buildTemplateConfig,
} from '../agentTemplates';

const emit = defineEmits(['created']);

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);

const formState = reactive({
  botName: '',
  botDescription: '',
  botAvatar: null,
  botAvatarUrl: '',
  botPersonaName: '',
  brandName: '',
  salutation: 'Kak',
  language: 'id',
  template: DEFAULT_TEMPLATE_KEY,
});

const templateKey = key => key.toUpperCase();

const languageOptions = computed(() => [
  { value: 'id', label: t('AI_AGENT.CREATE.FORM.LANGUAGE_OPTIONS.ID') },
  { value: 'en', label: t('AI_AGENT.CREATE.FORM.LANGUAGE_OPTIONS.EN') },
  { value: 'auto', label: t('AI_AGENT.CREATE.FORM.LANGUAGE_OPTIONS.AUTO') },
]);

const v$ = useVuelidate(
  {
    botName: {
      required: helpers.withMessage(
        () => t('AI_AGENT.CREATE.FORM.ERRORS.NAME'),
        required
      ),
    },
    botPersonaName: {
      required: helpers.withMessage(
        () => t('AI_AGENT.CREATE.FORM.ERRORS.BOT_NAME'),
        required
      ),
    },
  },
  formState
);

const isSubmitting = ref(false);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const botPersonaNameError = computed(() =>
  v$.value.botPersonaName.$error
    ? v$.value.botPersonaName.$errors[0]?.$message
    : ''
);

const resetForm = () => {
  Object.assign(formState, {
    botName: '',
    botDescription: '',
    botAvatar: null,
    botAvatarUrl: '',
    botPersonaName: '',
    brandName: '',
    salutation: 'Kak',
    language: 'id',
    template: DEFAULT_TEMPLATE_KEY,
  });
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = () => {
  formState.botAvatar = null;
  formState.botAvatarUrl = '';
};

const buildFormData = () => {
  const formData = new FormData();
  formData.append('name', formState.botName);
  formData.append('description', formState.botDescription);
  formData.append('bot_type', 'aegis');
  if (formState.botAvatar) {
    formData.append('avatar', formState.botAvatar);
  }
  formData.append(
    'bot_config',
    JSON.stringify(
      buildTemplateConfig(formState.template, {
        bot_name: formState.botPersonaName,
        brand_name: formState.brandName,
        salutation: formState.salutation,
        language: formState.language,
      })
    )
  );
  return formData;
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isSubmitting.value = true;
  try {
    const { data } = await AgentBotsAPI.create(buildFormData());
    store.dispatch('agentBots/get');
    useAlert(t('AI_AGENT.CREATE.API.SUCCESS_MESSAGE'));
    dialogRef.value.close();
    emit('created', data);
    resetForm();
  } catch (error) {
    useAlert(t('AI_AGENT.CREATE.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="$t('AI_AGENT.CREATE.TITLE')"
    :description="$t('AI_AGENT.CREATE.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <fieldset class="flex flex-col gap-2">
        <legend class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('AI_AGENT.CREATE.FORM.TEMPLATE.LABEL') }}
        </legend>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label
            v-for="template in AGENT_TEMPLATES"
            :key="template.key"
            :data-testid="`ai-agent-template-${template.key}`"
            class="flex cursor-pointer flex-col gap-1 rounded-xl border p-3 transition-colors"
            :class="
              formState.template === template.key
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-slate-7'
            "
          >
            <input
              v-model="formState.template"
              type="radio"
              name="ai-agent-template"
              :value="template.key"
              class="sr-only"
            />
            <span
              class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
            >
              <span :class="template.icon" class="size-4 text-n-slate-11" />
              {{
                $t(
                  `AI_AGENT.CREATE.FORM.TEMPLATE.OPTIONS.${templateKey(template.key)}.TITLE`
                )
              }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                $t(
                  `AI_AGENT.CREATE.FORM.TEMPLATE.OPTIONS.${templateKey(template.key)}.DESCRIPTION`
                )
              }}
            </span>
          </label>
        </div>
      </fieldset>

      <div class="mb-2 flex flex-col items-start">
        <span class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('AI_AGENT.CREATE.FORM.AVATAR.LABEL') }}
        </span>
        <Avatar
          :src="formState.botAvatarUrl"
          :name="formState.botName"
          :size="68"
          allow-upload
          icon-name="i-lucide-sparkles"
          @upload="handleImageUpload"
          @delete="handleAvatarDelete"
        />
      </div>

      <Input
        id="ai-agent-name"
        v-model="formState.botName"
        :label="$t('AI_AGENT.CREATE.FORM.NAME.LABEL')"
        :placeholder="$t('AI_AGENT.CREATE.FORM.NAME.PLACEHOLDER')"
        :message="botNameError"
        :message-type="botNameError ? 'error' : 'info'"
        @blur="v$.botName.$touch()"
      />

      <TextArea
        id="ai-agent-description"
        v-model="formState.botDescription"
        :label="$t('AI_AGENT.CREATE.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('AI_AGENT.CREATE.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <Input
        id="ai-agent-bot-name"
        v-model="formState.botPersonaName"
        :label="$t('AI_AGENT.CREATE.FORM.BOT_NAME.LABEL')"
        :placeholder="$t('AI_AGENT.CREATE.FORM.BOT_NAME.PLACEHOLDER')"
        :message="botPersonaNameError"
        :message-type="botPersonaNameError ? 'error' : 'info'"
        @blur="v$.botPersonaName.$touch()"
      />

      <Input
        id="ai-agent-brand-name"
        v-model="formState.brandName"
        :label="$t('AI_AGENT.CREATE.FORM.BRAND_NAME.LABEL')"
        :placeholder="$t('AI_AGENT.CREATE.FORM.BRAND_NAME.PLACEHOLDER')"
      />

      <Input
        id="ai-agent-salutation"
        v-model="formState.salutation"
        :label="$t('AI_AGENT.CREATE.FORM.SALUTATION.LABEL')"
        :placeholder="$t('AI_AGENT.CREATE.FORM.SALUTATION.PLACEHOLDER')"
      />

      <Select
        v-model="formState.language"
        :label="$t('AI_AGENT.CREATE.FORM.LANGUAGE.LABEL')"
        :options="languageOptions"
      />

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AI_AGENT.CREATE.FORM.CANCEL')"
          @click="dialogRef.close()"
        />
        <NextButton
          type="submit"
          data-testid="ai-agent-submit"
          :label="$t('AI_AGENT.CREATE.FORM.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
